import '../styles/mobile.css';
import { render } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  faArrowDown, faArrowLeft, faArrowUp, faArrowsRotate, faBullhorn, faCheck, faChevronRight, faCircleXmark, faEye, faEyeSlash, faGear,
  faCopy, faKey, faMagnifyingGlass, faMobileScreen, faRightFromBracket, faShieldHalved, faSliders, faStar, faTableList, faTriangleExclamation,
  faUsers, faWifi,
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '../admin/icons';
import type { Cell } from '../shared/protocol';
import { applyLegacyFormatter, applyRules, legacyFormatterFor, newResult } from '../shared/render/formatting';
import { needsHtml, sanitizeHtml } from '../shared/sanitize';
import {
  api, ApiError, forget, Offline, recall, remember, Unauthorized,
  type AdminUsers, type Catalog, type DataResponse, type Frame, type LoginReply, type MobileCard, type Prefs, type Publication,
} from './api';

// The phone app (dash.hph.hu): HPH employees log in with their extranet account and see the
// published dashboards as cards they can reorder, hide and star. Works offline with the last data.

const POLL_MS = 20_000;

// ── tiny router: /, /d/<dashboard>, /o/<dashboard> (its overview), /u (the admins' user list) ──

const listeners = new Set<() => void>();
window.addEventListener('popstate', () => listeners.forEach((f) => f()));

function go(path: string, replace = false): void {
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  listeners.forEach((f) => f());
  window.scrollTo(0, 0);
}

function usePath(): string {
  const [p, setP] = useState(location.pathname);
  useEffect(() => {
    const f = () => setP(location.pathname);
    listeners.add(f);
    return () => {
      listeners.delete(f);
    };
  }, []);
  return p;
}

// ── helpers ──────────────────────────────────────────────────────────────

function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return s + ' mp';
  const m = Math.round(s / 60);
  if (m < 60) return m + ' perc';
  const h = Math.floor(m / 60);
  if (h < 24) return h + ' óra';
  return Math.floor(h / 24) + ' nap';
}

function cellHtml(v: Cell | string): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (needsHtml(s)) return sanitizeHtml(s);
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

let formattersLoaded = -1;
function loadFormatters(version: number, done: () => void): void {
  if (version < 0 || version === formattersLoaded) return;
  const el = document.createElement('script');
  el.src = '/api/formatters.js?v=' + version;
  el.onload = () => {
    formattersLoaded = version;
    done();
  };
  document.head.appendChild(el);
}

/** The path of a start choice ('d:<id>' / 'o:<id>'), or null when it no longer exists. */
function startPath(catalog: Catalog, start: string | undefined): string | null {
  const m = /^([do]):(.+)$/.exec(start || '');
  if (!m) return null;
  const pub = catalog.publications.find((p) => p.id.toLowerCase() === m[2].toLowerCase());
  if (!pub || (m[1] === 'o' && !pub.overview)) return null;
  return '/' + m[1] + '/' + encodeURIComponent(pub.id);
}

// The start view opens once per launch, and only from the list's address: going back to the list
// from it stays on the list.
let startDone = false;

function applyTheme(p: Prefs | null): void {
  const t = p?.theme || 'auto';
  if (t === 'auto') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', t);
  document.documentElement.setAttribute('data-text', p?.text || 'm');
}

// ── app ───────────────────────────────────────────────────────────────────

function App() {
  const path = usePath();
  const [catalog, setCatalog] = useState<Catalog | null>(() => recall<Catalog>('catalog'));
  const [needLogin, setNeedLogin] = useState(false);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefsState] = useState<Prefs>(() => recall<Prefs>('prefs') || {});
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [, setFormatterTick] = useState(0);
  const saveTimer = useRef<number | undefined>(undefined);

  const load = async () => {
    try {
      const r = await api.catalog();
      const c = r.data!;
      setCatalog(c);
      remember('catalog', c);
      if (c.prefs) {
        setPrefsState(c.prefs);
        remember('prefs', c.prefs);
      }
      setNeedLogin(false);
      setOffline(false);
      setError(null);
      setLoadedOnce(true);
      loadFormatters(c.scriptsVersion, () => setFormatterTick((x) => x + 1));
    } catch (e) {
      if (e instanceof Unauthorized) {
        setNeedLogin(true);
        setCatalog(null);
      } else if (e instanceof Offline) setOffline(true);
      else setError((e as Error).message);
    }
  };

  useEffect(() => {
    load();
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  useEffect(() => applyTheme(prefs), [prefs]);

  // Open the chosen start view. Saved prefs decide at once; without a saved choice the server's
  // answer is awaited (a new phone has nothing saved yet).
  useEffect(() => {
    if (startDone || !catalog || (!prefs.start && !loadedOnce)) return;
    startDone = true;
    const target = location.pathname === '/' ? startPath(catalog, prefs.start) : null;
    if (target) go(target);
  }, [catalog, loadedOnce]);

  const setPrefs = (next: Prefs) => {
    setPrefsState(next);
    remember('prefs', next);
    clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      api.savePrefs(next).catch(() => {
        /* saved locally; the next change retries */
      });
    }, 800);
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* the cookie goes anyway */
    }
    forget('');
    setCatalog(null);
    setPrefsState({});
    setNeedLogin(true);
    go('/', true);
  };

  if (needLogin) return <Login onDone={load} />;
  if (!catalog) {
    return (
      <div class="center">
        {offline ? <p><Icon icon={faWifi} /> Nincs kapcsolat a szerverrel, és még nincs elmentett adat.</p> : error ? <p class="bad">{error}</p> : <div class="spinner" />}
        {(offline || error) && <button onClick={load}>Újra</button>}
      </div>
    );
  }

  const m = path.match(/^\/([do])\/([^/]+)/);
  const pub = m ? catalog.publications.find((p) => p.id.toLowerCase() === decodeURIComponent(m[2]).toLowerCase()) : null;
  const overview = m?.[1] === 'o' && pub?.overview ? pub : null;

  return (
    <>
      {offline && <div class="offline-bar"><Icon icon={faWifi} /> Nincs kapcsolat a szerverrel — a legutóbbi adatok látszanak</div>}
      {path === '/u' && catalog.isAdmin ? (
        <UsersView onUnauthorized={() => setNeedLogin(true)} />
      ) : overview ? (
        <OverviewView pub={overview} publications={catalog.publications} prefs={prefs} setPrefs={setPrefs} onOffline={setOffline} onUnauthorized={() => setNeedLogin(true)} />
      ) : pub && m?.[1] === 'd' ? (
        <DashboardView pub={pub} prefs={prefs} setPrefs={setPrefs} onOffline={setOffline} onUnauthorized={() => setNeedLogin(true)} />
      ) : (
        <Home catalog={catalog} prefs={prefs} setPrefs={setPrefs} logout={logout} />
      )}
    </>
  );
}

// ── login ────────────────────────────────────────────────────────────────

function Login({ onDone }: { onDone: () => void }) {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After the password: the authenticator's code, the first time with setting it up.
  const [second, setSecond] = useState<LoginReply | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const digits = code.replace(/\D/g, '');

  const fail = (err: unknown) => {
    if (err instanceof ApiError && err.status === 410) {
      setSecond(null);
      setCode('');
    }
    setError(err instanceof Offline ? 'Nincs kapcsolat a szerverrel. Próbáld újra később.' : (err as Error).message);
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!user.trim() || !password) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.login(user.trim(), password);
      if (r.data?.step) {
        setSecond(r.data);
        setCode('');
      } else {
        setPassword('');
        onDone();
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: Event) => {
    e.preventDefault();
    if (!second?.ticket || digits.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await api.loginCode(second.ticket, digits);
      setPassword('');
      setSecond(null);
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(second?.key || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the key is on the screen anyway */
    }
  };

  const brand = (sub: string) => (
    <div class="brand"><img src="/icons/icon-192.png" alt="" width={48} height={48} /><div><b>HPH Dashboard</b><span>{sub}</span></div></div>
  );

  if (second) {
    const enroll = second.step === 'enroll';
    return (
      <div class="login">
        <form class="login-card" onSubmit={submitCode}>
          {brand(enroll ? 'kétlépcsős belépés beállítása' : 'kétlépcsős belépés')}
          {enroll ? (
            <div class="enroll">
              <p>A belépéshez egy hitelesítő alkalmazás kódja is kell — például Microsoft Authenticator vagy Google Authenticator. Egyszer kell beállítani:</p>
              <a class="button wide" href={second.uri}><Icon icon={faMobileScreen} /> Hozzáadás a hitelesítőhöz (ezen a telefonon)</a>
              <div class="enroll-or">vagy olvasd be másik eszközzel:</div>
              <img class="qr" src={second.qr} alt="QR-kód a hitelesítő alkalmazásnak" width={200} height={200} />
              <div class="enroll-or">vagy írd be kézzel ezt a kulcsot:</div>
              <div class="key-row">
                <code class="key">{(second.key || '').replace(/(.{4})/g, '$1 ').trim()}</code>
                <button type="button" class="small" onClick={copyKey}><Icon icon={copied ? faCheck : faCopy} /> {copied ? 'Másolva' : 'Másolás'}</button>
              </div>
              <p>Utána írd be ide az alkalmazás által mutatott 6 jegyű kódot.</p>
            </div>
          ) : (
            <p class="hint"><Icon icon={faKey} /> Írd be a hitelesítő alkalmazásod 6 jegyű kódját ({user.trim()}).</p>
          )}
          <label>
            <span>Kód</span>
            <input class="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} autoFocus={!enroll}
              onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/[^\d ]/g, ''))} />
          </label>
          {error && <div class="error"><Icon icon={faTriangleExclamation} /> {error}</div>}
          <button class="primary" type="submit" disabled={busy || digits.length !== 6}>{busy ? 'Ellenőrzés…' : enroll ? 'Beállítás és belépés' : 'Belépés'}</button>
          <button type="button" class="link" onClick={() => { setSecond(null); setCode(''); setError(null); }}>Vissza</button>
        </form>
      </div>
    );
  }

  return (
    <div class="login">
      <form class="login-card" onSubmit={submit}>
        {brand('raktári dashboardok')}
        <label>
          <span>Felhasználónév</span>
          <input autoComplete="username" autoCapitalize="none" spellcheck={false} value={user} onInput={(e) => setUser((e.target as HTMLInputElement).value)} autoFocus />
        </label>
        <label>
          <span>Jelszó</span>
          <input type="password" autoComplete="current-password" value={password} onInput={(e) => setPassword((e.target as HTMLInputElement).value)} />
        </label>
        {error && <div class="error"><Icon icon={faTriangleExclamation} /> {error}</div>}
        <button class="primary" type="submit" disabled={busy}>{busy ? 'Belépés…' : 'Belépés'}</button>
        <p class="hint">A HPH extranet (hph.hu) felhasználóneveddel és jelszavaddal lépj be. HPH dolgozóknak.</p>
      </form>
    </div>
  );
}

// ── the admins' user list ───────────────────────────────────────────────────

/** Lower case without accents, for a search that finds "Ungor" from "ungor" and "Ügyeletes" from "ugy". */
const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const TOTP_POLICY: Record<string, string> = { off: 'kikapcsolva', admins: 'csak a mobil adminoknak', all: 'mindenkinek' };

function UsersView({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [data, setData] = useState<AdminUsers | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.adminUsers();
      if (r.data) setData(r.data);
      setError(null);
    } catch (e) {
      if (e instanceof Unauthorized) onUnauthorized();
      else setError(e instanceof Offline ? 'Nincs kapcsolat a szerverrel.' : (e as Error).message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const act = async (kind: 'logout' | 'totp', user: string, name: string) => {
    // Two taps: the first only arms the button.
    if (confirm !== kind + ':' + user) {
      setConfirm(kind + ':' + user);
      return;
    }
    setBusy(true);
    try {
      if (kind === 'logout') {
        const r = await api.adminLogout(user);
        setNotice(`${name}: ${r.data?.ended ?? 0} munkamenet lezárva`);
      } else {
        await api.adminResetTotp(user);
        setNotice(`${name}: hitelesítő törölve — a következő belépéskor újra beállítja`);
      }
      await load();
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy(false);
      setConfirm(null);
      setTimeout(() => setNotice(null), 5000);
    }
  };

  const users = data?.users || [];
  const terms = fold(q).split(/\s+/).filter(Boolean);
  const list = terms.length ? users.filter((u) => terms.every((t) => fold(u.name + ' ' + u.user).includes(t))) : users;
  const shown = list.slice(0, 150);
  const now = data?.now ?? Date.now();

  return (
    <div class="page">
      <header class="bar">
        <button class="icon" aria-label="Vissza" onClick={() => (history.length > 1 ? history.back() : go('/'))}><Icon icon={faArrowLeft} /></button>
        <div class="bar-title">
          <b>Felhasználók</b>
          <span>{data ? `${users.length} belépni jogosult · hitelesítő: ${TOTP_POLICY[data.totp] ?? data.totp}` : 'betöltés…'}</span>
        </div>
        <button class="icon" aria-label="Frissítés" onClick={load}><Icon icon={faArrowsRotate} /></button>
      </header>

      <div class="search">
        <Icon icon={faMagnifyingGlass} />
        <input type="search" placeholder="Név vagy azonosító (pl. 810567)" value={q} autoFocus
          onInput={(e) => { setQ((e.target as HTMLInputElement).value); setOpen(null); setConfirm(null); }} />
      </div>

      {data && (
        <p class="muted small users-summary">
          {users.filter((u) => u.totpSince).length} hitelesítővel · {users.filter((u) => u.sessions > 0).length} bejelentkezve
          {terms.length > 0 && <> · {list.length} találat</>}
        </p>
      )}
      {notice && <div class="notice">{notice}</div>}
      {error && <div class="error page-error"><Icon icon={faTriangleExclamation} /> {error}</div>}

      <ul class="users">
        {shown.map((u) => (
          <li class={open === u.user ? 'open' : ''}>
            <button class="urow" onClick={() => { setOpen(open === u.user ? null : u.user); setConfirm(null); }}>
              <span class="uname">{u.name}</span>
              <span class="uid">{u.user}</span>
              <span class="umeta">
                {u.lastLogin ? 'belépett ' + ago(now - u.lastLogin) + ' ezelőtt' : 'még nem lépett be'}
                {u.sessions > 0 && <> · {u.sessions} munkamenet</>}
                {u.failed24h > 0 && <span class="bad"> · {u.failed24h} hibás (24 óra)</span>}
              </span>
              <span class="badges">
                {u.totpSince ? <span class="badge good"><Icon icon={faShieldHalved} /> hitelesítő</span> : u.totpRequired ? <span class="badge warn">hitelesítő: még nincs</span> : null}
                {u.admin && <span class="badge">admin</span>}
                {u.mustChange && <span class="badge bad">jelszócsere kell</span>}
              </span>
            </button>
            {open === u.user && (
              <div class="uactions">
                <button class={confirm === 'logout:' + u.user ? 'danger armed' : ''} disabled={busy || u.sessions === 0} onClick={() => act('logout', u.user, u.name)}>
                  <Icon icon={faRightFromBracket} /> {confirm === 'logout:' + u.user ? 'Biztosan kijelentkezteted?' : 'Kijelentkeztetés'}
                </button>
                <button class={confirm === 'totp:' + u.user ? 'danger armed' : 'danger'} disabled={busy || !u.totpSince} onClick={() => act('totp', u.user, u.name)}>
                  <Icon icon={faKey} /> {confirm === 'totp:' + u.user ? 'Biztosan törlöd?' : 'Hitelesítő törlése'}
                </button>
                {u.totpSince && <span class="muted small">beállítva {ago(now - u.totpSince)} ezelőtt</span>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {list.length > shown.length && <p class="muted small users-summary">…és még {list.length - shown.length} — szűkíts a keresésen.</p>}
    </div>
  );
}

// ── home ─────────────────────────────────────────────────────────────────

function sitesOf(c: Catalog) {
  const seen = new Map<string, string>();
  for (const p of c.publications) for (const s of p.sites) if (!seen.has(s.id)) seen.set(s.id, s.name);
  return Array.from(seen, ([id, name]) => ({ id, name }));
}

function Home({ catalog, prefs, setPrefs, logout }: { catalog: Catalog; prefs: Prefs; setPrefs: (p: Prefs) => void; logout: () => void }) {
  const [settings, setSettings] = useState(false);
  const sites = sitesOf(catalog);
  const site = prefs.site && sites.some((s) => s.id === prefs.site) ? prefs.site : sites[0]?.id;
  const favs = new Set(prefs.favorites || []);
  const overviews = catalog.publications.filter((p) => p.overview);
  const overviewSites = (p: Publication) =>
    [p, ...(p.overview!.others || []).map((o) => catalog.publications.find((x) => x.id === o.publication))]
      .flatMap((x) => (x ? x.sites.map((s) => s.name) : []));
  const list = catalog.publications
    .filter((p) => p.sites.length === 0 || !site || p.sites.some((s) => s.id === site))
    .slice()
    .sort((a, b) => Number(favs.has(b.id)) - Number(favs.has(a.id)) || a.sort - b.sort || a.title.localeCompare(b.title, 'hu'));

  const toggleFav = (id: string) => {
    const f = new Set(favs);
    if (f.has(id)) f.delete(id);
    else f.add(id);
    setPrefs({ ...prefs, favorites: Array.from(f) });
  };

  return (
    <div class="page">
      <header class="bar">
        <div class="bar-title"><b>HPH Dashboard</b><span>{catalog.fullName}</span></div>
        <button class="icon" aria-label="Beállítások" onClick={() => setSettings(!settings)}><Icon icon={faGear} /></button>
      </header>

      {settings && <SettingsSheet catalog={catalog} prefs={prefs} setPrefs={setPrefs} logout={logout} onClose={() => setSettings(false)} />}

      {overviews.length > 0 && (
        <ul class="pubs">
          {overviews.map((p) => (
            <li class="overview-entry">
              <button class="pub" onClick={() => go('/o/' + encodeURIComponent(p.id))}>
                <span class="pub-title"><Icon icon={faTableList} /> {p.overview!.title}</span>
                <span class="pub-desc">{p.title} · {overviewSites(p).join(', ') || 'egy nézet'}</span>
                <Icon icon={faChevronRight} class="chev" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {sites.length > 1 && (
        <div class="chips" role="tablist" aria-label="Telephely">
          {sites.map((s) => (
            <button role="tab" aria-selected={s.id === site} class={s.id === site ? 'chip on' : 'chip'} onClick={() => setPrefs({ ...prefs, site: s.id })}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {list.length ? (
        <ul class="pubs">
          {list.map((p) => (
            <li>
              <button class="pub" onClick={() => go('/d/' + encodeURIComponent(p.id))}>
                <span class="pub-title">{p.title}</span>
                {p.description && <span class="pub-desc">{p.description}</span>}
                <span class="pub-meta">{p.cards.length} elem</span>
                <Icon icon={faChevronRight} class="chev" />
              </button>
              <button class={'star' + (favs.has(p.id) ? ' on' : '')} aria-label={favs.has(p.id) ? 'Kedvenc eltávolítása' : 'Kedvencnek jelölés'} aria-pressed={favs.has(p.id)} onClick={() => toggleFav(p.id)}>
                <Icon icon={faStar} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p class="empty">Ehhez a telephelyhez még nincs publikált dashboard.</p>
      )}
    </div>
  );
}

function SettingsSheet({ catalog, prefs, setPrefs, logout, onClose }: { catalog: Catalog; prefs: Prefs; setPrefs: (p: Prefs) => void; logout: () => void; onClose: () => void }) {
  const start = startPath(catalog, prefs.start) ? prefs.start! : '';
  return (
    <div class="sheet">
      <label class="sheet-row">
        <span>Kezdőképernyő</span>
        <select value={start} onChange={(e) => setPrefs({ ...prefs, start: (e.target as HTMLSelectElement).value || undefined })}>
          <option value="">Lista (ez a képernyő)</option>
          {catalog.publications.filter((p) => p.overview).map((p) => <option value={'o:' + p.id}>{p.overview!.title} – {p.title}</option>)}
          {catalog.publications.map((p) => <option value={'d:' + p.id}>{p.title}</option>)}
        </select>
      </label>
      <div class="sheet-row">
        <span>Betűméret</span>
        <div class="seg">
          {(['s', 'm', 'l'] as const).map((t) => (
            <button class={(prefs.text || 'm') === t ? 'on' : ''} onClick={() => setPrefs({ ...prefs, text: t })}>{t === 's' ? 'Kicsi' : t === 'm' ? 'Közepes' : 'Nagy'}</button>
          ))}
        </div>
      </div>
      <div class="sheet-row">
        <span>Téma</span>
        <div class="seg">
          {(['auto', 'light', 'dark'] as const).map((t) => (
            <button class={(prefs.theme || 'auto') === t ? 'on' : ''} onClick={() => setPrefs({ ...prefs, theme: t })}>{t === 'auto' ? 'Rendszer' : t === 'light' ? 'Világos' : 'Sötét'}</button>
          ))}
        </div>
      </div>
      {catalog.totp && <div class="sheet-row muted small"><span><Icon icon={faShieldHalved} /> Kétlépcsős belépés: be van állítva</span></div>}
      {catalog.isAdmin && (
        <div class="sheet-row">
          <button onClick={() => { onClose(); go('/u'); }}><Icon icon={faUsers} /> Felhasználók (admin)</button>
        </div>
      )}
      <div class="sheet-row">
        <button class="danger" onClick={logout}><Icon icon={faRightFromBracket} /> Kijelentkezés</button>
        <button onClick={onClose}><Icon icon={faCheck} /> Kész</button>
      </div>
    </div>
  );
}

// ── one dashboard ────────────────────────────────────────────────────────

function DashboardView(props: { pub: Publication; prefs: Prefs; setPrefs: (p: Prefs) => void; onOffline: (v: boolean) => void; onUnauthorized: () => void }) {
  const { pub, prefs, setPrefs } = props;
  const sites = pub.sites;
  const site = sites.length === 0 ? '' : prefs.site && sites.some((s) => s.id === prefs.site) ? prefs.site : sites[0].id;
  const key = 'data.' + pub.id + '.' + site;
  const [data, setData] = useState<DataResponse | null>(() => recall<DataResponse>(key));
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const etag = useRef<string | undefined>(undefined);
  const refresh = useRef<() => void>(() => {});

  useEffect(() => {
    etag.current = undefined;
    setData(recall<DataResponse>(key));
    let stop = false;
    const fetchNow = async () => {
      if (document.hidden) return;
      setLoading(true);
      try {
        const r = await api.data(pub.id, site, etag.current);
        if (stop) return;
        if (r.status !== 304 && r.data) {
          setData(r.data);
          remember(key, r.data);
        }
        etag.current = r.etag || undefined;
        props.onOffline(false);
      } catch (e) {
        if (e instanceof Unauthorized) props.onUnauthorized();
        else if (e instanceof Offline) props.onOffline(true);
      } finally {
        if (!stop) setLoading(false);
      }
    };
    refresh.current = () => {
      etag.current = undefined;
      fetchNow();
    };
    fetchNow();
    const t = setInterval(fetchNow, POLL_MS);
    const vis = () => !document.hidden && fetchNow();
    document.addEventListener('visibilitychange', vis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', vis);
    };
  }, [pub.id, site]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // The person's order and hidden cards for this dashboard.
  const order = prefs.order?.[pub.id] || [];
  const hidden = new Set(prefs.hidden?.[pub.id] || []);
  const cards = useMemo(() => {
    const pos = new Map(order.map((id, i) => [id, i] as [string, number]));
    return pub.cards
      .map((c, i) => ({ c, i }))
      .sort((a, b) => (pos.get(a.c.id) ?? 1e6 + a.i) - (pos.get(b.c.id) ?? 1e6 + b.i))
      .map((x) => x.c);
  }, [pub, prefs.order?.[pub.id]?.join(',')]);

  const move = (id: string, delta: number) => {
    const ids = cards.map((c) => c.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setPrefs({ ...prefs, order: { ...(prefs.order || {}), [pub.id]: ids } });
  };
  const toggleHidden = (id: string) => {
    const h = new Set(hidden);
    if (h.has(id)) h.delete(id);
    else h.add(id);
    setPrefs({ ...prefs, hidden: { ...(prefs.hidden || {}), [pub.id]: Array.from(h) } });
  };
  const reset = () => {
    const o = { ...(prefs.order || {}) };
    const h = { ...(prefs.hidden || {}) };
    delete o[pub.id];
    delete h[pub.id];
    setPrefs({ ...prefs, order: o, hidden: h });
  };

  const newest = data ? Math.max(0, ...Object.values(data.frames).map((f) => f.at || 0)) : 0;

  return (
    <div class="page">
      <header class="bar">
        <button class="icon" aria-label="Vissza" onClick={() => (history.length > 1 ? history.back() : go('/'))}><Icon icon={faArrowLeft} /></button>
        <div class="bar-title">
          <b>{pub.title}</b>
          <span>{newest ? 'frissítve ' + ago(now - newest) + ' ezelőtt' : 'adatra vár…'}</span>
        </div>
        <button class={'icon' + (loading ? ' spin' : '')} aria-label="Frissítés" onClick={() => refresh.current()}><Icon icon={faArrowsRotate} /></button>
        <button class={'icon' + (editing ? ' on' : '')} aria-label="Elrendezés" aria-pressed={editing} onClick={() => setEditing(!editing)}><Icon icon={faSliders} /></button>
      </header>

      {sites.length > 1 && (
        <div class="chips">
          {sites.map((s) => (
            <button class={s.id === site ? 'chip on' : 'chip'} onClick={() => setPrefs({ ...prefs, site: s.id })}>{s.name}</button>
          ))}
        </div>
      )}

      {editing && (
        <div class="edit-bar">
          <span>Sorrend és láthatóság — csak neked</span>
          <button class="link" onClick={reset}>Alaphelyzet</button>
          <button class="primary small" onClick={() => setEditing(false)}><Icon icon={faCheck} /> Kész</button>
        </div>
      )}

      <div class="cards">
        {cards.map((c, i) => {
          const isHidden = hidden.has(c.id);
          if (isHidden && !editing) return null;
          const feed = c.feeds[site] ?? c.feeds[''];
          const frame = feed ? data?.frames[feed] : undefined;
          return (
            <section class={'card' + (isHidden ? ' hidden-card' : '')}>
              <div class="card-head">
                <h2>{c.title || c.query || 'Elem'}</h2>
                {!editing && frame && <Status frame={frame} now={now} />}
                {editing && (
                  <div class="card-tools">
                    <button class="icon" aria-label="Feljebb" disabled={i === 0} onClick={() => move(c.id, -1)}><Icon icon={faArrowUp} /></button>
                    <button class="icon" aria-label="Lejjebb" disabled={i === cards.length - 1} onClick={() => move(c.id, 1)}><Icon icon={faArrowDown} /></button>
                    <button class="icon" aria-label={isHidden ? 'Megjelenítés' : 'Elrejtés'} onClick={() => toggleHidden(c.id)}><Icon icon={isHidden ? faEyeSlash : faEye} /></button>
                  </div>
                )}
              </div>
              {!editing && <CardBody card={c} frame={frame} />}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function Status({ frame, now }: { frame: Frame; now: number }) {
  if (frame.status === 'ok' || frame.status === 'pending') return frame.at ? <span class="age">{ago(now - frame.at)}</span> : null;
  const text = frame.status === 'down' ? 'a rendszer nem elérhető' : frame.status === 'disabled' ? 'kikapcsolva' : 'frissítési hiba';
  return <span class="age bad" title={frame.error}><Icon icon={frame.status === 'down' ? faCircleXmark : faTriangleExclamation} /> {text}</span>;
}

// ── the overview: every site of a publication in one table ──────────────

const numberFormat = new Intl.NumberFormat('hu-HU');

/** The first number of a frame's first row ("Doboz szám:", 302698 → 302698). */
function firstNumber(frame?: Frame): number | null {
  for (const v of frame?.rows[0] || []) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const s = v.replace(/[\s ]/g, '').replace(',', '.');
      if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
    }
  }
  return null;
}

const fresh = (f?: Frame) => !!f && (f.status === 'ok' || f.status === 'pending');

function OverviewView(props: { pub: Publication; publications: Publication[]; prefs: Prefs; setPrefs: (p: Prefs) => void; onOffline: (v: boolean) => void; onUnauthorized: () => void }) {
  const { pub, prefs, setPrefs } = props;
  const ov = pub.overview!;
  const key = 'overview.' + pub.id;
  const [data, setData] = useState<DataResponse | null>(() => recall<DataResponse>(key));
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const etag = useRef<string | undefined>(undefined);
  const refresh = useRef<() => void>(() => {});

  useEffect(() => {
    etag.current = undefined;
    let stop = false;
    const fetchNow = async () => {
      if (document.hidden) return;
      setLoading(true);
      try {
        const r = await api.overview(pub.id, etag.current);
        if (stop) return;
        if (r.status !== 304 && r.data) {
          setData(r.data);
          remember(key, r.data);
        }
        etag.current = r.etag || undefined;
        props.onOffline(false);
      } catch (e) {
        if (e instanceof Unauthorized) props.onUnauthorized();
        else if (e instanceof Offline) props.onOffline(true);
      } finally {
        if (!stop) setLoading(false);
      }
    };
    refresh.current = () => {
      etag.current = undefined;
      fetchNow();
    };
    fetchNow();
    const t = setInterval(fetchNow, POLL_MS);
    const vis = () => !document.hidden && fetchNow();
    document.addEventListener('visibilitychange', vis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', vis);
    };
  }, [pub.id]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  const frameIn = (p: Publication, card: string | null | undefined, site: string) => {
    const c = card ? p.cards.find((x) => x.id === card) : undefined;
    const feed = c && (c.feeds[site] ?? c.feeds['']);
    return feed ? data?.frames[feed] : undefined;
  };
  const frameOf = (card: string, site: string) => frameIn(pub, card, site);
  const sitesIn = (p: Publication) => (p.sites.length ? p.sites : [{ id: '', name: p.title }]);
  const sites = sitesIn(pub);

  // The message cards: each different text once, with the sites it belongs to when not all.
  const messages: { text: string; sites: string[] }[] = [];
  for (const id of ov.messages) {
    for (const s of sites) {
      const f = frameOf(id, s.id);
      const text = (f?.rows || [])
        .map((r) => r.filter((v) => v !== null && v !== '').map(String).join(' '))
        .filter(Boolean)
        .join('\n');
      if (!text) continue;
      const known = messages.find((x) => x.text === text);
      if (known) known.sites.push(s.name);
      else messages.push({ text, sites: [s.name] });
    }
  }

  // This publication's sites, then those of the other dashboards (HBW_Main for BLK3 …), each with
  // its own card per column; a row opens the dashboard it came from.
  const sources = [
    { p: pub, cards: ov.columns.map((c) => c.card as string | null) },
    ...(ov.others || []).flatMap((o) => {
      const p = props.publications.find((x) => x.id === o.publication);
      return p ? [{ p, cards: o.cards }] : [];
    }),
  ];
  const rows = sources.flatMap(({ p, cards }) =>
    sitesIn(p).map((s) => {
      const frames = cards.map((card) => frameIn(p, card, s.id));
      const trouble = frames.find((f) => f && !fresh(f));
      return { site: s, pubId: p.id, frames, values: frames.map(firstNumber), has: cards.map((c) => !!c), trouble };
    }),
  );
  const totals = ov.columns.map((_, i) => rows.reduce((sum, r) => sum + (r.values[i] ?? 0), 0));
  // A column a dashboard has no card for (HBW's boxes) is not "missing" — only stale or empty ones are.
  const partial = ov.columns.map((_, i) => rows.some((r) => r.has[i] && (r.values[i] === null || !fresh(r.frames[i]))));

  // Header groups: neighbouring columns of one group share a cell above them.
  const groups: { label?: string; span: number; first: number }[] = [];
  ov.columns.forEach((c, i) => {
    const last = groups[groups.length - 1];
    if (last && c.group && last.label === c.group) last.span++;
    else groups.push({ label: c.group, span: 1, first: i });
  });
  const grouped = groups.some((g) => g.label);

  const allFrames = Object.values(data?.frames || {});
  const newest = allFrames.length ? Math.max(0, ...allFrames.map((f) => f.at || 0)) : 0;
  const open = (site: string, pubId: string) => {
    if (site) setPrefs({ ...prefs, site });
    go('/d/' + encodeURIComponent(pubId));
  };

  return (
    <div class="page">
      <header class="bar">
        <button class="icon" aria-label="Vissza" onClick={() => (history.length > 1 ? history.back() : go('/'))}><Icon icon={faArrowLeft} /></button>
        <div class="bar-title">
          <b>{ov.title}</b>
          <span>{newest ? pub.title + ' · frissítve ' + ago(now - newest) + ' ezelőtt' : 'adatra vár…'}</span>
        </div>
        <button class={'icon' + (loading ? ' spin' : '')} aria-label="Frissítés" onClick={() => refresh.current()}><Icon icon={faArrowsRotate} /></button>
      </header>

      <div class="cards">
        {messages.map((m) => (
          <section class="card message">
            <Icon icon={faBullhorn} />
            <div>
              <div class="message-text">{m.text}</div>
              {m.sites.length < sites.length && <div class="muted small">{m.sites.join(', ')}</div>}
            </div>
          </section>
        ))}

        {ov.columns.length > 0 && (
          <section class="card">
            {!data ? (
              <div class="muted small">Adatra vár…</div>
            ) : (
              <div class="tbl-wrap">
                <table class="overview">
                  <thead>
                    <tr>
                      <th rowSpan={grouped ? 2 : 1}>Raktár</th>
                      {grouped
                        ? groups.map((g) => (g.label ? <th class="grp" colSpan={g.span}>{g.label}</th> : <th class="a-right" rowSpan={2}>{ov.columns[g.first].label}</th>))
                        : ov.columns.map((c) => <th class="a-right">{c.label}</th>)}
                    </tr>
                    {grouped && (
                      <tr>{ov.columns.map((c) => (c.group ? <th class="a-right">{c.label}</th> : null))}</tr>
                    )}
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr onClick={() => open(r.site.id, r.pubId)}>
                        <th scope="row">
                          {r.site.name}
                          {r.trouble && <span class="bad-mark" title={r.trouble.error}> <Icon icon={r.trouble.status === 'down' ? faCircleXmark : faTriangleExclamation} /></span>}
                        </th>
                        {r.values.map((v, i) => (
                          <td class={'a-right' + (!r.has[i] || !fresh(r.frames[i]) ? ' stale' : '')}>{v === null ? '–' : numberFormat.format(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {ov.totals && rows.length > 1 && (
                    <tfoot>
                      <tr>
                        <th scope="row">Összesen</th>
                        {totals.map((t, i) => <td class="a-right">{numberFormat.format(t)}{partial[i] ? '*' : ''}</td>)}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
            <div class="muted small overview-note">
              {ov.totals && partial.some(Boolean) && <>* nem minden raktár adata friss · </>}
              Egy raktárra koppintva megnyílik a dashboardja.
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ── card bodies ──────────────────────────────────────────────────────────

const ROWS_SHOWN = 30;

function prepare(card: MobileCard, frame: Frame) {
  const cfg = card.columns || {};
  const srcKeys = frame.cols.map((c) => c[0]);
  let order = srcKeys.map((_, i) => i);
  if (cfg.order?.length) {
    const pos = new Map(cfg.order.map((k, i) => [k.toLowerCase(), i] as [string, number]));
    order = order.slice().sort((a, b) => (pos.get(srcKeys[a].toLowerCase()) ?? 1e6 + a) - (pos.get(srcKeys[b].toLowerCase()) ?? 1e6 + b));
  }
  const keys = order.map((i) => srcKeys[i]);
  const labelCfg: Record<string, string> = {};
  for (const [k, v] of Object.entries(cfg.labels || {})) labelCfg[k.toLowerCase()] = v;
  const labels = order.map((i) => labelCfg[srcKeys[i].toLowerCase()] ?? frame.cols[i][1]);
  const rows: Cell[][] = frame.rows.map((r) => order.map((i) => (i < r.length ? r[i] : null)));

  const out = newResult();
  for (const h of cfg.hidden || []) out.hiddenCols.add(h.toLowerCase());
  applyRules(card.rules, keys, rows, out);
  const fn = legacyFormatterFor(card.query);
  if (fn) applyLegacyFormatter(fn, keys, labels, rows, out, () => { /* a formatter bug must not blank the card */ });
  const lower = keys.map((k) => k.toLowerCase());
  const visible = lower.map((k) => !out.hiddenCols.has(k));
  const align: Record<string, string> = {};
  for (const [k, v] of Object.entries(cfg.align || {})) align[k.toLowerCase()] = v;
  return { keys, lower, labels, rows, out, visible, align };
}

function CardBody({ card, frame }: { card: MobileCard; frame?: Frame }) {
  const [all, setAll] = useState(false);
  if (card.kind === 'text') return <div class="text-card">{card.text}</div>;
  if (!frame || (!frame.at && frame.status === 'pending')) return <div class="muted small">Adatra vár…</div>;

  const p = prepare(card, frame);
  if (p.out.hideWidget) return <div class="muted small">Nincs megjeleníthető adat.</div>;
  const rowIdx = p.rows.map((_, i) => i).filter((i) => !p.out.hiddenRows.has(i));
  if (rowIdx.length === 0) return <div class="muted small">Nincs sor.</div>;

  if (card.kind === 'kpi') {
    return (
      <div class="kpi">
        {rowIdx.map((r) => (
          <div class={'kpi-row ' + (p.out.rowClass.get(r) || '')}>
            {p.keys.map((_, c) => (p.visible[c] ? <span class={'kpi-cell ' + (p.out.cellClass.get(r + ':' + p.lower[c]) || '')} dangerouslySetInnerHTML={{ __html: cellHtml(p.rows[r][c]) }} /> : null))}
          </div>
        ))}
      </div>
    );
  }

  const shown = all ? rowIdx : rowIdx.slice(0, ROWS_SHOWN);
  const total = Math.max(frame.total, frame.rows.length);
  return (
    <>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>{p.labels.map((l, c) => (p.visible[c] ? <th class={p.align[p.lower[c]] ? 'a-' + p.align[p.lower[c]] : ''} dangerouslySetInnerHTML={{ __html: cellHtml(l) }} /> : null))}</tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr class={p.out.rowClass.get(r) || ''}>
                {p.keys.map((_, c) => {
                  if (!p.visible[c]) return null;
                  const cls = [p.out.cellClass.get(r + ':' + p.lower[c]), p.align[p.lower[c]] ? 'a-' + p.align[p.lower[c]] : ''].filter(Boolean).join(' ');
                  return <td class={cls} dangerouslySetInnerHTML={{ __html: cellHtml(p.rows[r][c]) }} />;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(rowIdx.length > shown.length || total > frame.rows.length) && (
        <div class="more">
          {rowIdx.length > shown.length && <button class="link" onClick={() => setAll(true)}>Mind a {rowIdx.length} sor</button>}
          {total > frame.rows.length && <span class="muted small"> · {total} sorból {frame.rows.length} érhető el mobilon</span>}
        </div>
      )}
    </>
  );
}

// ── boot ─────────────────────────────────────────────────────────────────

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* the app works without it, just not offline */
    });
  });
}

render(<App />, document.getElementById('app')!);
